import React, { Fragment, useRef } from 'react';
import {
  ChatArea,
  ScrollableContent,
  NoResultsMessage,
  Overlay,
} from '../../styles/messenger/layout.style';
import {TimestampSeparator, ReadReceipt} from '../../styles/messenger/messenger.style';
import { ClipLoader } from 'react-spinners';

import { ChatHeader } from './ChatHeader';
import { ChatMessage } from './ChatMessage';
import { ReplyPreview } from './ReplyPreview';
import { MessageInputBar } from './MessageInputBar';
import { MessageStatus } from './MessageStatus';

import { Connection } from '../../hooks/useConnections';
import { Message } from '../../hooks/useMessages';
import { formatTimestamp, shouldShowTimestamp } from '../../utils/formatDate';
import { MessageStatuses } from '../../hooks/useMessageStatuses';
import { UserDetails } from '../../types/schemas/abc';
import { TuteeLoader } from '../common/TuteeLoader';
import { getFirstName } from '../../utils/name';

type UserProfile = Connection['parent'] | Connection['tutor'];

interface ChatPaneProps {
    activeConnectionId: string | null;
    activeConnection: Connection | undefined;
    currentUser: UserDetails | null;
    messages: Message[];
    messagesIsLoading: boolean;
    otherUser: UserProfile | null;
    replyingTo: Message | null;
    onStartReply: (message: Message) => void;
    onCancelReply: () => void;
    onSendMessage: (content: string) => void;
    onRetry: (message: Message) => void;
    onProfileClick: () => void;
    onAddLessonClick: () => void;
    messageStatuses: MessageStatuses;
    scrollContainerRef: React.RefObject<HTMLDivElement>;
    onReturn: () => void; 
    onBlur?: () => void; 
    onFocus?: (e: React.FocusEvent) => void;
    isAdminConnection?: boolean;
}

export const ChatPane = ({
    activeConnectionId,
    activeConnection,
    currentUser,
    messages,
    messagesIsLoading,
    otherUser,
    replyingTo,
    onStartReply,
    onCancelReply,
    onSendMessage,
    onRetry,
    onProfileClick,
    onAddLessonClick,
    messageStatuses,
    scrollContainerRef, 
    onReturn, 
    onBlur, 
    onFocus,
    isAdminConnection = false,
    } : ChatPaneProps) => {

    const messagesEndRef = useRef<HTMLDivElement>(null);

    return (
        <ChatArea   
            onFocus={onFocus}
            onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) {
                    onBlur && onBlur();
                }
         }}>
            <ChatHeader
                currentUser={currentUser}
                otherUser={otherUser}
                onProfileClick={onProfileClick}
                onAddLessonClick={onAddLessonClick}
                onReturn={onReturn}
                isAdminConnection={isAdminConnection}
            />

            <ScrollableContent ref={scrollContainerRef}>
                {activeConnectionId ? (
                messagesIsLoading ? (
                    <Overlay>
                    <TuteeLoader />
                    </Overlay>
                ) : (
                    <>
                    {messages.map((message, index) => {
                        const prevMessage = index > 0 ? messages[index - 1] : null;
                        const showTimestampSeparator = shouldShowTimestamp(prevMessage, message);
                        const isNewSpeakerGroup =
                        !prevMessage || prevMessage.senderId !== message.senderId;
                        const addExtraMargin = isNewSpeakerGroup && !showTimestampSeparator;

                        const repliedToSenderName =
                        message.replyTo && activeConnection
                            ? message.replyTo.senderId === activeConnection.parent.id
                            ? getFirstName(activeConnection.parent.name)
                            : getFirstName(activeConnection.tutor.name)
                            : null;

                        return (
                        <Fragment key={message.id}>
                            {showTimestampSeparator && (
                            <TimestampSeparator>
                                {formatTimestamp(message, prevMessage)}
                            </TimestampSeparator>
                            )}
                            <ChatMessage
                            message={message}
                            repliedToSenderName={repliedToSenderName}
                            currentUserId={currentUser!.id}
                            onRetry={onRetry}
                            isNewGroup={addExtraMargin}
                            onStartReply={onStartReply}
                            />

                            {message.id === messageStatuses.seenMessageId && (
                            <ReadReceipt>
                                <MessageStatus status="read" />
                            </ReadReceipt>
                            )}

                            {message.id === messageStatuses.lastSentIfFinalFailed && (
                            <ReadReceipt>
                                <MessageStatus status="sent" />
                            </ReadReceipt>
                            )}

                            {messageStatuses.finalStatus &&
                            message.id === messageStatuses.finalStatus.messageId && (
                                <ReadReceipt>
                                <MessageStatus status={messageStatuses.finalStatus.status} />
                                </ReadReceipt>
                            )}
                        </Fragment>
                        );
                    })}
                    <div ref={messagesEndRef} />
                    </>
                )
                ) : (
                <NoResultsMessage>
                    <h2>No Active Conversation</h2>
                    <p>Select a conversation to start chatting.</p>
                </NoResultsMessage>
                )}
            </ScrollableContent>

            {replyingTo && activeConnection && (
                <ReplyPreview
                message={replyingTo}
                senderName={
                    replyingTo.senderId === activeConnection.parent.id
                    ? getFirstName(activeConnection.parent.name)
                    : getFirstName(activeConnection.tutor.name)
                }
                onCancel={onCancelReply}
                />
            )}

            {activeConnectionId && <MessageInputBar onSendMessage={onSendMessage} />}
        </ChatArea>
    );
}